// import React from 'react';
// import ContestDetails from '../details/ContestDetails';

/**
 * Calculates estimated payouts for a contest when official payouts aren't available
 * This function exactly matches the admin calculateDynamicPayouts logic
 * @param {number} entryFee - The entry fee per participant
 * @param {number} numberOfEntrants - The total number of participants
 * @returns {number[]} - An array of payout amounts for each position
 */
export const calculateEstimatedPayouts = (entryFee, numberOfEntrants) => {
  // Calculate the total prize pool (entry fee * numberOfEntrants with no platform fee)
  // This matches how the admin function handles it where totalPrizePool is passed directly
  const totalPrizePool = entryFee * numberOfEntrants;
  
  // Define payout percentage limits
  const minPayoutPercentage = 0.1; // Minimum 10% of entrants
  const maxPayoutPercentage = 0.2; // Maximum 20% of entrants
  
  // Calculate the number of payout positions
  const minPayoutPositions = Math.ceil(numberOfEntrants * minPayoutPercentage);
  const maxPayoutPositions = Math.ceil(numberOfEntrants * maxPayoutPercentage);
  const payoutPositions = Math.max(minPayoutPositions, Math.min(maxPayoutPositions, numberOfEntrants));
  
  // Calculate initial payouts
  const payouts = [];
  let remainingPrizePool = totalPrizePool;
  
  // Distribute the prize pool with a simple decreasing percentage
  for (let i = 0; i < payoutPositions; i++) {
    const percentage = (payoutPositions - i) / (payoutPositions * (payoutPositions + 1) / 2);
    const payoutAmount = Math.round((totalPrizePool * percentage) * 100) / 100;
    payouts.push(payoutAmount);
    remainingPrizePool -= payoutAmount;
  }
  
  // Adjust the last payout to ensure the total matches the prize pool
  if (remainingPrizePool !== 0) {
    payouts[payouts.length - 1] += Math.round(remainingPrizePool * 100) / 100;
  }
  
  return payouts;
};

export const getOrdinalSuffix = (i) => {
    const j = i % 10;
    const k = i % 100;
    if (j === 1 && k !== 11) {
        return i + "st";
    }
    if (j === 2 && k !== 12) {
        return i + "nd";
    }
    if (j === 3 && k !== 13) {
        return i + "rd";
    }
    return i + "th";
};

export const calculateCurrentPayouts = (totalPrizePool, numberOfEntrants) => {
  // Define the payout structure (from Sheet2)
  const payoutTable = {
    "<=5": [1.0],
    "<=15": [0.65, 0.35],
    "<=30": [0.5, 0.3, 0.2],
    "<=40": [0.45, 0.27, 0.17, 0.11],
    "<=50": [0.41, 0.25, 0.15, 0.11, 0.08],
    "<=60": [0.39, 0.23, 0.14, 0.1, 0.08, 0.06],
    "<=70": [0.37, 0.22, 0.135, 0.095, 0.075, 0.06, 0.045],
    "<=100": [0.35, 0.2, 0.13, 0.09, 0.07, 0.055, 0.045, 0.035, 0.025],
    "<=120": [0.33, 0.19, 0.125, 0.085, 0.0675, 0.045, 0.0375, 0.03, 0.0225, 0.0175, 0.0175, 0.0175],
    "<=150": [0.32, 0.185, 0.12, 0.082, 0.0635, 0.049, 0.039, 0.029, 0.025, 0.0165, 0.0165, 0.0165, 0.0125, 0.0125, 0.0125],
    "<=180": [0.31, 0.18, 0.115, 0.081, 0.0625, 0.0475, 0.037, 0.0275, 0.0195, 0.0155, 0.0155, 0.0155, 0.013, 0.013, 0.013, 0.0115, 0.0115, 0.0115],
    "<=270": [0.28, 0.165, 0.11, 0.0765, 0.057, 0.044, 0.034, 0.025, 0.018, 0.014, 0.014, 0.014, 0.012, 0.012, 0.012, 0.0105, 0.0105, 0.0105, 0.009, 0.009, 0.009, 0.009, 0.009, 0.009, 0.009, 0.009, 0.009],
    "<=360": [0.27, 0.165, 0.105, 0.0725, 0.0535, 0.0425, 0.0325, 0.0235, 0.017, 0.013, 0.013, 0.013, 0.0105, 0.0105, 0.0105, 0.008, 0.008, 0.008, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006],
    "<=450": [0.264, 0.155, 0.105, 0.07, 0.0505, 0.0405, 0.031, 0.022, 0.016, 0.012, 0.012, 0.012, 0.0095, 0.0095, 0.0095, 0.008, 0.008, 0.008, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.007, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006, 0.006, 0.005, 0.005, 0.005, 0.005, 0.005, 0.004, 0.004, 0.004, 0.0035],
    "<=540": [
      0.2486, 0.144, 0.0955, 0.064, 0.0445, 0.033, 0.027, 0.0205, 0.0155, 0.013,
    ],
    "<=630": [
      0.2452, 0.143, 0.095, 0.0635, 0.044, 0.0325, 0.0265, 0.02, 0.015, 0.0125,
    ],
    "<=720": [
      0.2394, 0.14, 0.0945, 0.063, 0.0435, 0.032, 0.026, 0.0195, 0.0145, 0.012,
    ],
    "<=810": [
      0.2308, 0.1357, 0.0931, 0.063, 0.0435, 0.032, 0.026, 0.0195, 0.0145,
      0.012,
    ],
  };

  // Determine which column to use
  const eligibleKeys = Object.keys(payoutTable).filter((k) => {
    const maxPlayers = parseInt(k.replace("<=", ""));
    return numberOfEntrants <= maxPlayers;
  });

  if (eligibleKeys.length === 0) {
    throw new Error("Number of entrants exceeds supported payout structure.");
  }

  const selectedKey = eligibleKeys[0];
  const payoutPercents = payoutTable[selectedKey];

  // Calculate dollar payouts
  const payouts = payoutPercents.map(
    (percent) => +(percent * totalPrizePool).toFixed(2)
  );
  return payouts;
};
  
// Format a number with commas and no decimal places
export const formatNumber = (number) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(number);
};

// Remove the renderContestDetails function
/*
export const renderContestDetails = (contest, showEnterButton = false) => {
  return <ContestDetails contest={contest} showEnterButton={showEnterButton} />;
};
*/

// Add new utility function
export const formatDisplayURL = (url) => {
    const fullURL = url.startsWith('http') ? url : `https://${url}`;
    return new URL(fullURL).hostname;
};